import { useLocation } from 'react-router-dom'
import { useContext, useEffect } from 'react'
import { useShallow } from 'zustand/react/shallow'

import Login from './login'
import { useInput } from '../../hooks/useInput'
import { NotificationContext, notifyOpen } from '../../components/ui/notification/notification.provider'
import { useLogin } from '../../store/login/login.state'
import { setMask, validForm } from '../../hooks/useForm'
import { ILogin } from './login.types'
import { DictNameType, ISelectDictionaryContainer } from '../../components/smart/selectDictionary/selectDictionary.type'
import { IModalConfirm } from '../../components/dumb/modal.confirm'
import { IModalSMSContainer } from '../../components/smart/modalSMS/modalSMS.type'


/** Авторизация пользователя - Контейнер */
const LoginContainer = () => {
    const urlParams = useLocation()
    const notify = useContext(NotificationContext)

    const searchParams = new URLSearchParams(urlParams.search)

    const {isLoading, error, actionLogin, actionCourseName, actionProgramInfo, actionApplicationNumber} = useLogin(useShallow((state) => ({
        isLoading: state.isLoading,
        error: state.error,
        actionLogin: state.actionLogin,
        actionCourseName: state.actionCourseName,
        actionProgramInfo: state.actionProgramInfo,
        actionApplicationNumber: state.actionApplicationNumber,
    })))

    const txtApplicationNumber = useInput<string>('')
    const txtClientName = useInput<string>('')
    const txtClientPhone = useInput<string>('')

    const txtCourseID = useInput<string | number>('')
    const txtCourseName = useInput<string>('')
    
    const txtCourseProgramID = useInput<number | string>('')
    const txtVolumeHours = useInput<string>('')
    const txtProgramName = useInput<string>('')
    
    const filesState = useInput<File[]>([])
    const modalConfirm = useInput<IModalConfirm>({isOpen: false})
    const modalSms = useInput<IModalSMSContainer>({isOpen: false})
    const modalDict = useInput<ISelectDictionaryContainer>({isOpen: false})
    

    useEffect(() => {
        const clientName = decodeURIComponent(searchParams.get('name') || '')
        const clientPhone = decodeURIComponent(searchParams.get('phone') || '')
        const clientCourseID = decodeURIComponent(searchParams.get('courseID') || '')
        const clientCourseProgramID = decodeURIComponent(searchParams.get('courseProgramID') || '')

        if (clientName && clientName !== 'null' && clientName !== 'undefined') {
            txtClientName.setValue(clientName)
        }
        
        if (clientPhone && clientPhone !== 'null' && clientPhone !== 'undefined') { 
            txtClientPhone.setValue(clientPhone)

            actionApplicationNumber(clientPhone).then((res) => {
                if (res?.applicationNumber) {
                    txtApplicationNumber.setValue(res?.applicationNumber)
                }
            })
        }

        if (clientCourseID) {
            actionCourseName(clientCourseID).then((res) => {
                if (res?.courseName) {
                    txtCourseID.setValue(clientCourseID)
                    txtCourseName.setValue(res?.courseName)
                }
            })
        }

        if (clientCourseProgramID) {
            actionProgramInfo(clientCourseProgramID).then((res) => {
                if (res?.volumeHours) {
                    let typeDurationEducation: string = 'недели'
                    if (Number(res?.volumeHours) >= 250) {
                        typeDurationEducation = 'месяца'
                        if (Number(res?.durationEducation) >= 5) typeDurationEducation = 'месяцев'
                    }


                    txtCourseProgramID.setValue(clientCourseProgramID)
                    txtVolumeHours.setValue(`${res?.volumeHours} (${res?.durationEducation} ${typeDurationEducation})`)
                    txtProgramName.setValue(res.programName || '')
                }
            })
        }
    
    }, [searchParams]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (error) return notify && notifyOpen(error?.message, error?.type, 4000, notify)
    }, [error]) // eslint-disable-line react-hooks/exhaustive-deps

    // Загрузка файлов на сервер
    const fileUploader = (fileList: File[]) => {
        const banExtansion = [
            'msg', 'csv',
            'mp3', 'wma', 'snd', 'wav', 'ra', 'au', 'aac', 'mp4', '3gp', 'avi', 'mpg', 'mov', 'wmv',
            'c', 'cpp', 'java', 'py', 'js', 'cs', 'swift', 'dta', 'pl', 'sh', 'bat', 'com', 'exe', 'msi',
            'hqx', 'arj', 'tar', 'arc', 'sit', 'gz', 'z',
            'xhtml', 'asp', 'css', 'aspx', 'rss', 'rar', 'zip', '7z', 'bzip2', 'gzip', 'tar', 'wim', 'xz'
        ] // Расширения файлов, запрещенные для загрузки

        const maxSize = 10485760 // Максимально разрешенный размер файла для загрузки (10 MB)

        if (fileList.length > 10) return notify && notifyOpen(`Максимальное количество файлов разрешенных для загрузки равна 10. Вы пытаетесь загрузить ${fileList.length}.`, 'error', 4000, notify)

        filesState.setValue(currentFiles => {
            // Создаем массив имен файлов из текущего списка файлов
            const existingFileNames = currentFiles.map(file => file.name)

            // Фильтруем новые файлы, оставляя только те, чье имя не совпадает с именами файлов в текущем списке
            const newFiles = fileList.filter(file => {
                const fileNameExtension = file.name.toLowerCase().split('.').pop() || ''
                const isExtensionAllowed = !banExtansion.includes(fileNameExtension)
                const isFileSizeAllowed = file.size < maxSize
                const isFileNameUnique = !existingFileNames.includes(file.name)

                // Проверка на максимальное количество файлов
                const isBelowMaxFilesCount = currentFiles.length + 1 <= 10 // Добавляем 1, так как текущий файл еще не включен в currentFiles

                if (!isFileSizeAllowed) {
                    notify && notifyOpen(`Файл ${file.name}, превышает допустимый размер в 10 мегабайт, его загрузка не возможна.`, 'error', 4000, notify)
                }
                if (!isExtensionAllowed) {
                    notify && notifyOpen(`Файл ${file.name}, имеет запрещённое расширение, его загрузка не возможна.`, 'error', 4000, notify)
                }
                if (!isFileNameUnique) {
                    notify && notifyOpen(`Файл с именем ${file.name} уже существует в списке.`, 'error', 4000, notify)
                }
                if (!isBelowMaxFilesCount) {
                    notify && notifyOpen(`Превышено максимальное количество файлов, максимум разрешено загружать 10 файлов.`, 'error', 4000, notify)
                }

                return isExtensionAllowed && isFileSizeAllowed && isFileNameUnique && isBelowMaxFilesCount
            })

            // Объединяем новые файлы с текущим списком
            const updatedFiles = [...currentFiles, ...newFiles]

            // Возвращаем обновленный список файлов
            return updatedFiles
        })
    }

    /** Удаление файла из списка по имени */
    const deleteFileByName = (fileNameToDelete: string) => {
        filesState.setValue(currentFiles => {
            return currentFiles.filter(file => file.name !== fileNameToDelete)
        })
    }

    /** Открытие/закрытие окна подтверждения */
    const handlerOpenModalConfirm = (isOpen: boolean, headerText: string, contentText: string) => {
        modalConfirm.setValue({
            isOpen, headerText, contentText, 
            handlerButtonCancel() {modalConfirm.setValue({isOpen: false})}, 
            handlerButtonOk() {
                modalConfirm.setValue({isOpen: false})
            }
        })
    }

    /** Открытие/закрытие окна подтверждения SMS */
    const handlerOpenModalSMS = (isOpen: boolean) => {
        modalSms.setValue({
            isOpen,
            handlerButtonCancel() {modalSms.setValue({isOpen: false})}, 
            async handlerButtonOk(verifCode) {
                const result = await actionLogin(verifCode, 'sendedForm', txtClientName.value, txtClientPhone.value, txtCourseID.value.toString(), txtCourseProgramID.value.toString(), filesState.value)
                if (result) {
                    modalSms.setValue({isOpen: false})
                    window.location.href = 'https://modskill.ru'
                }
            },

            async handlerButtonRepeat () {
                const result = await actionLogin(0, 'verifCode', txtClientName.value, txtClientPhone.value, txtCourseID.value.toString(), txtCourseProgramID.value.toString(), filesState.value)
                if (!result) handlerOpenModalSMS(false)
            }, 
        })
    }

    /** Открытие/закрытие окна выбора справочника */
    const handlerOpenModalDict = (isOpen: boolean, dictName: DictNameType, whereID?: string | number) => {
        modalDict.setValue({
            isOpen, dictName, whereID,
            handlerButtonCancel() {modalDict.setValue({isOpen: false})},
            returnSelectedData(id, name, substring) {
                switch (dictName) {
                    case 'courses': {
                        txtCourseID.setValue(id)
                        txtCourseName.setValue(name)

                        txtCourseProgramID.setValue('')
                        txtVolumeHours.setValue('')
                        txtProgramName.setValue('')
                        break
                    }
                    case 'programs': {
                        console.log(name)
                        txtCourseProgramID.setValue(id)
                        txtVolumeHours.setValue(name)
                        txtProgramName.setValue(substring || '')
                        break
                    }
                }

                modalDict.setValue({isOpen: false})
            }
        })
    }
    
    /** Нажатие на кнопку отправить */
    const handleButtonClickLogin = async () => {
        const validate = validForm([
            {fieldName: 'Ваше имя', value: txtClientName.value.toString(), typeName: 'clientName', requiredField: true},
            {fieldName: 'Ваш номер телефона', value: txtClientPhone.value.toString(), typeName: 'phone_number_mask', requiredField: true},
            {fieldName: 'Курс', value: txtCourseID.value.toString(), typeName: 'course', requiredField: true},
            {fieldName: 'Программа', value: txtCourseProgramID.value.toString(), typeName: 'program', requiredField: true},
        ])

        // Выводим ошибки валидации, если они есть
        for (const item of validate) { notify && notifyOpen(item, 'warning', 4000, notify) }
        if (filesState.value.length === 0) return notify && notifyOpen('Необходимо прикрепить хотя бы один файл.', 'warning', 4000, notify)

        if (validate.length === 0) {
            const result = await actionLogin(0, 'verifCode', txtClientName.value, txtClientPhone.value, txtCourseID.value.toString(), txtCourseProgramID.value.toString(), filesState.value)
            if (result) handlerOpenModalSMS(true)
        }
    }


    const handleButtonClickTelegram = () => {
        const validate = validForm([
            {fieldName: 'Ваше имя', value: txtClientName.value.toString(), typeName: 'clientName', requiredField: true},
            {fieldName: 'Ваш номер телефона', value: txtClientPhone.value.toString(), typeName: 'phone_number_mask', requiredField: true},
            {fieldName: 'Курс', value: txtCourseID.value.toString(), typeName: 'course', requiredField: true},
            {fieldName: 'Программа', value: txtCourseProgramID.value.toString(), typeName: 'program', requiredField: true},
        ])

        // Выводим ошибки валидации, если они есть
        for (const item of validate) { notify && notifyOpen(item, 'warning', 4000, notify) }

        if (validate.length === 0) {
            const message = `**** СОВРЕМЕННАЯ АКАДЕМИЯ ****%0AПодача документов%0A----------------------------%0AИмя: ${txtClientName.value || 'Не указано'}%0AТелефон: ${txtClientPhone.value.replace(/\s/g, '').replace('+', '%2B') || 'Не указан'}%0A----------------------------%0AЗаявка: ${txtApplicationNumber.value || 'Не зарегистрирована'}%0A----------------------------%0AКурс: ${txtCourseName.value || 'Не выбран'}%0AПрограмма: ${txtProgramName.value || 'Не выбрана'}%0AОбъём программы: ${txtVolumeHours.value || 'Не выбран'}%0A----------------------------`
            window.location.href = `tg://resolve?domain=iktbox&text=${message}`
        }
    }

    const txtPhoneChange = (phoneNumber: string) => {
        const resultPhoneMask = setMask(phoneNumber)
        txtClientPhone.setValue(resultPhoneMask.data)
    }

    /** Свойства передаваемые в компонену */
    const propsToComponent: ILogin = {
        isLoading, txtClientName, txtClientPhone, 
        txtCourseID, txtCourseName, txtApplicationNumber,
        txtVolumeHours, txtProgramName, filesState,
        modalConfirm, handlerOpenModalConfirm,
        modalDict, handlerOpenModalDict,
        modalSms, 
        handleButtonClickLogin, txtPhoneChange, fileUploader, deleteFileByName, handleButtonClickTelegram
    }

    return <Login {...propsToComponent} />
}

export default LoginContainer